import { type KeyboardEvent, type MouseEvent, useCallback } from 'react';

import { useRouter, useRouterActions } from 'react-resource-router';

import { clearHash } from '../../../common/deep-link/clearHash';
import { scrollToTop } from '../../../common/deep-link/scrollToTop';
import { isModifiedClick } from '../../utils/is-modified-click';

/**
 * Shared click behaviour for a page link, used by both the inline
 * `NavItemLink` and the hamburger's `PageLinkItem`: pushing the route the
 * visitor is already on is a no-op, so scroll to the top instead (matching
 * `BrandHomeLink`), otherwise navigate there. Also reports whether the item
 * is the current route, so both consumers can mark it the same way
 * (`aria-current="page"` on the inline link, `isSelected` on the menu item).
 */
export const usePageItemClick = (
	path: string,
	routeName: string,
): {
	isActive: boolean;
	onClick: (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
} => {
	const [{ route }] = useRouter();
	const { push } = useRouterActions();
	const isActive = route?.name === routeName;

	const onClick = useCallback(
		(event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();

			if (isActive) {
				scrollToTop();
				clearHash();
				return;
			}

			push(path);
		},
		[isActive, path, push],
	);

	return { isActive, onClick };
};
